import { useState, useEffect, useCallback, useRef } from 'react';
import api, { landingDemoAPI } from '../../api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
  Loader2, Upload, ExternalLink, Film, Image, Mail,
  Search, Download, Trash2, Eye, Copy, Phone,
} from 'lucide-react';
import { ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';

const EVENT_LABELS = {
  demo_video_view: 'Reproducciones', demo_video_25: 'Vieron 25%', demo_video_50: 'Vieron 50%',
  demo_video_75: 'Vieron 75%', demo_video_complete: 'Completaron', demo_contact_click: 'Clic en contacto',
  demo_plans_click: 'Clic en planes', demo_guided_open: 'Demo guiada', demo_request_submitted: 'Solicitudes enviadas',
};

export default function LandingDemoPage() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [requests, setRequests] = useState([]);
  const [requestCounts, setRequestCounts] = useState({});
  const [reqFilter, setReqFilter] = useState('all');
  const [reqSearch, setReqSearch] = useState('');
  const [detailRequest, setDetailRequest] = useState(null);
  const [detailNotes, setDetailNotes] = useState('');
  const [detailSaving, setDetailSaving] = useState(false);
  const [daily, setDaily] = useState([]);
  const fileRefs = { video_desktop: useRef(), video_mobile: useRef(), video_webm: useRef(), poster: useRef() };

  const loadRequests = useCallback(async () => {
    try {
      const params = {};
      if (reqFilter !== 'all') params.estado = reqFilter;
      if (reqSearch) params.q = reqSearch;
      const { data } = await landingDemoAPI.listRequests(params);
      setRequests(data.items || []);
      setRequestCounts(data.counts_by_estado || {});
    } catch (e) {
      toast.error('Error al cargar solicitudes');
    }
  }, [reqFilter, reqSearch]);

  const load = useCallback(async () => {
    try {
      const [c, a, dl] = await Promise.all([
        api.get('/landing-demo/config'),
        api.get('/landing-demo/analytics'),
        api.get('/landing-demo/analytics/daily?days=30'),
      ]);
      setConfig(c.data);
      setAnalytics(a.data);
      setDaily(dl.data.series || []);
    } catch (e) { toast.error('Error al cargar la configuración'); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRequests(); }, [loadRequests]);

  const set = (k, v) => setConfig(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/landing-demo/config', config);
      setConfig(data);
      toast.success('Configuración guardada');
    } catch (e) { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const upload = async (tipo, file) => {
    if (!file) return;
    setUploading(tipo);
    try {
      const fd = new FormData();
      fd.append('tipo', tipo);
      fd.append('file', file);
      const { data } = await api.post('/landing-demo/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setConfig(prev => ({ ...prev, [data.field]: data.url }));
      toast.success(`Archivo subido (${data.size_mb} MB)`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al subir el archivo'); }
    finally { setUploading(null); }
  };

  if (!config) return <Loader2 className="animate-spin mx-auto my-12" />;

  // ==================== Requests helpers ====================
  const ESTADO_META = {
    nueva: { label: 'Nueva', color: 'bg-blue-100 text-blue-700 border-blue-300' },
    contactado: { label: 'Contactado', color: 'bg-amber-100 text-amber-700 border-amber-300' },
    convertido: { label: 'Convertido', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
    descartado: { label: 'Descartado', color: 'bg-slate-100 text-slate-500 border-slate-300' },
  };

  const openDetail = (r) => {
    setDetailRequest(r);
    setDetailNotes(r.notas_internas || '');
  };

  const updateRequestField = async (id, patch) => {
    try {
      const { data } = await landingDemoAPI.updateRequest(id, patch);
      toast.success('Solicitud actualizada');
      // Update in-place for snappy UI
      setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, ...data } : r)));
      if (detailRequest?.id === id) setDetailRequest({ ...detailRequest, ...data });
      // refresh counts
      loadRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar');
    }
  };

  const saveDetail = async () => {
    if (!detailRequest) return;
    setDetailSaving(true);
    try {
      await updateRequestField(detailRequest.id, { notas_internas: detailNotes });
      setDetailRequest(null);
    } finally {
      setDetailSaving(false);
    }
  };

  const removeRequest = async (id) => {
    if (!window.confirm('¿Eliminar esta solicitud definitivamente?')) return;
    try {
      await landingDemoAPI.deleteRequest(id);
      toast.success('Solicitud eliminada');
      setRequests((prev) => prev.filter((r) => r.id !== id));
      loadRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  const copyEmail = (email) => {
    if (!email) return;
    navigator.clipboard.writeText(email).then(
      () => toast.success('Correo copiado'),
      () => toast.error('No se pudo copiar')
    );
  };

  const downloadCsv = () => {
    const params = {};
    if (reqFilter !== 'all') params.estado = reqFilter;
    if (reqSearch) params.q = reqSearch;
    const url = landingDemoAPI.exportCsvUrl(params);
    const token = localStorage.getItem('token');
    // Fetch as blob to attach auth header (StreamingResponse serves CSV)
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.ok ? res.blob() : Promise.reject())
      .then((blob) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `solicitudes_demo_${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
      })
      .catch(() => toast.error('No se pudo descargar el CSV'));
  };

  const totalRequests = Object.values(requestCounts).reduce((a, b) => a + (b || 0), 0);

  const UploadBtn = ({ tipo, label, accept, current }) => (
    <div className="flex items-center justify-between border rounded-lg p-3">
      <div>
        <div className="text-sm font-medium flex items-center gap-2">
          {tipo === 'poster' ? <Image size={16} /> : <Film size={16} />} {label}
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          {current ? <Badge variant="secondary" className="text-xs">{current.split('/').pop()}</Badge> : 'Sin archivo — se usa la demo animada integrada'}
        </div>
      </div>
      <div className="flex gap-2">
        <input type="file" accept={accept} ref={fileRefs[tipo]} className="hidden"
          onChange={e => upload(tipo, e.target.files?.[0])} data-testid={`upload-input-${tipo}`} />
        <Button size="sm" variant="outline" disabled={uploading === tipo}
          onClick={() => fileRefs[tipo].current?.click()} data-testid={`upload-btn-${tipo}`}>
          {uploading === tipo ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
        </Button>
        {current && (
          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => set(
            { video_desktop: 'video_url', video_mobile: 'video_mobile_url', video_webm: 'video_webm_url', poster: 'poster_url' }[tipo], null)}>
            Quitar
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="landing-demo-admin">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Landing Page — Demo Comercial</h1>
          <p className="text-sm text-slate-500">Configura el video "Conoce SimuClinic en 30 segundos" de la landing pública.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.open('/#demo', '_blank')} className="gap-1">
            <ExternalLink size={14} /> Ver en landing
          </Button>
          <Button variant="outline" onClick={() => window.open('/demo-stand', '_blank')} className="gap-1">
            <ExternalLink size={14} /> Modo Stand
          </Button>
          <Button onClick={save} disabled={saving} data-testid="save-demo-config-btn">
            {saving && <Loader2 size={14} className="animate-spin mr-1" />} Guardar
          </Button>
        </div>
      </div>

      <Tabs defaultValue="config">
        <TabsList>
          <TabsTrigger value="config" data-testid="tab-demo-config">Configuración</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-demo-analytics">Analítica</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-demo-requests">Solicitudes ({totalRequests})</TabsTrigger>
        </TabsList>

        <TabsContent value="config" className="grid md:grid-cols-2 gap-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Contenido</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div><Label>Título</Label><Input value={config.titulo || ''} onChange={e => set('titulo', e.target.value)} data-testid="demo-config-titulo" /></div>
              <div><Label>Descripción / Subtítulo</Label><Input value={config.subtitulo || ''} onChange={e => set('subtitulo', e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Botón principal</Label><Input value={config.boton_principal || ''} onChange={e => set('boton_principal', e.target.value)} /></div>
                <div><Label>Botón secundario</Label><Input value={config.boton_secundario || ''} onChange={e => set('boton_secundario', e.target.value)} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Duración (segundos)</Label><Input type="number" value={config.duracion || 30} onChange={e => set('duracion', parseInt(e.target.value) || 30)} /></div>
                <div><Label>Fecha de publicación</Label><Input type="date" value={config.fecha_publicacion || ''} onChange={e => set('fecha_publicacion', e.target.value)} /></div>
              </div>
              <div>
                <Label className="flex items-center gap-1"><Mail size={14} /> Correo del equipo comercial</Label>
                <Input type="email" value={config.notificacion_email || ''} onChange={e => set('notificacion_email', e.target.value)}
                  placeholder="comercial@simuclinic.cl" data-testid="demo-config-notificacion-email" />
                <p className="text-xs text-slate-500 mt-1">Recibirá un correo por cada solicitud de demostración. Dejar vacío para desactivar.</p>
              </div>
              <div className="space-y-2 pt-2">
                {[['activo', 'Sección activa en la landing'], ['autoplay', 'Autoplay al entrar en pantalla (siempre sin sonido)'],
                  ['loop', 'Loop (repetición automática — congresos/stands)'], ['sonido_inicial', 'Sonido inicial en Modo Stand'],
                  ['respuesta_automatica', 'Respuesta automática al solicitante (correo de bienvenida)']].map(([k, l]) => (
                  <div key={k} className="flex items-center justify-between">
                    <Label className="text-sm">{l}</Label>
                    <Switch checked={!!config[k]} onCheckedChange={v => set(k, v)} data-testid={`demo-config-${k}`} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Archivos de video</CardTitle>
              <CardDescription className="text-xs">
                MP4 H.264 1920×1080 (16:9), ~30 segundos. Si no hay video subido, la landing muestra la demo animada integrada.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <UploadBtn tipo="video_desktop" label="Video escritorio (MP4, 16:9)" accept=".mp4" current={config.video_url} />
              <UploadBtn tipo="video_mobile" label="Video móvil opcional (MP4, 9:16)" accept=".mp4" current={config.video_mobile_url} />
              <UploadBtn tipo="video_webm" label="Versión WebM opcional" accept=".webm" current={config.video_webm_url} />
              <UploadBtn tipo="poster" label="Poster / portada" accept=".png,.jpg,.jpeg,.webp" current={config.poster_url} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="demo-analytics-grid">
            {analytics && Object.entries(EVENT_LABELS).map(([k, l]) => (
              <Card key={k}><CardContent className="p-4 text-center">
                <div className="text-2xl font-bold">{analytics.eventos?.[k] ?? 0}</div>
                <div className="text-xs text-slate-500">{l}</div>
              </CardContent></Card>
            ))}
            {analytics && (
              <Card className="border-cyan-300"><CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-cyan-600">{analytics.tasa_finalizacion}%</div>
                <div className="text-xs text-slate-500">Tasa de finalización</div>
              </CardContent></Card>
            )}
          </div>

          <Card data-testid="demo-daily-chart">
            <CardHeader>
              <CardTitle className="text-base">Vistas y solicitudes por día (últimos 30 días)</CardTitle>
              <CardDescription className="text-xs">Mide el impacto de tus campañas comerciales día a día.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={daily} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="fecha" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} interval={Math.max(0, Math.floor(daily.length / 10) - 1)} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip labelFormatter={(v) => new Date(v + 'T12:00:00').toLocaleDateString('es-CL', { day: 'numeric', month: 'short' })} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="vistas" name="Vistas del video" fill="#06b6d4" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="completadas" name="Videos completados" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
                    <Line type="monotone" dataKey="solicitudes" name="Solicitudes de demo" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="clics_contacto" name="Clics en contacto" stroke="#10b981" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="requests" className="space-y-4">
          {/* Toolbar: search + filters + export */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
                <div className="flex-1 relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Buscar por nombre, correo, institución…"
                    value={reqSearch}
                    onChange={(e) => setReqSearch(e.target.value)}
                    className="pl-9"
                    data-testid="req-search-input"
                  />
                </div>
                <div className="flex flex-wrap gap-1.5 items-center">
                  {['all', 'nueva', 'contactado', 'convertido', 'descartado'].map((k) => {
                    const count = k === 'all' ? totalRequests : (requestCounts[k] || 0);
                    return (
                      <Button
                        key={k}
                        variant={reqFilter === k ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setReqFilter(k)}
                        className="h-8 text-xs gap-1.5"
                        data-testid={`req-filter-${k}`}
                      >
                        <span>{k === 'all' ? 'Todas' : ESTADO_META[k]?.label || k}</span>
                        <Badge variant="secondary" className="text-[10px] px-1.5">{count}</Badge>
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadCsv}
                  className="gap-1 h-8"
                  disabled={requests.length === 0}
                  data-testid="btn-export-csv"
                >
                  <Download size={14} /> Exportar CSV
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <div className="border rounded-lg overflow-x-auto bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Correo</TableHead>
                  <TableHead>Institución</TableHead>
                  <TableHead>Cargo</TableHead>
                  <TableHead>Usuarios</TableHead>
                  <TableHead className="w-24 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-sm text-slate-500 py-8">
                      {reqSearch || reqFilter !== 'all'
                        ? 'Ninguna solicitud coincide con los filtros.'
                        : 'Aún no hay solicitudes de demostración.'}
                    </TableCell>
                  </TableRow>
                ) : requests.map((r) => {
                  const meta = ESTADO_META[r.estado || 'nueva'] || ESTADO_META.nueva;
                  return (
                    <TableRow
                      key={r.id}
                      className="cursor-pointer hover:bg-slate-50"
                      data-testid={`req-row-${r.id}`}
                    >
                      <TableCell className="text-xs whitespace-nowrap" onClick={() => openDetail(r)}>
                        {new Date(r.created_at).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Select
                          value={r.estado || 'nueva'}
                          onValueChange={(v) => updateRequestField(r.id, { estado: v })}
                        >
                          <SelectTrigger className={`h-7 text-xs w-32 ${meta.color}`} data-testid={`req-status-${r.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(ESTADO_META).map(([k, m]) => (
                              <SelectItem key={k} value={k}>{m.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-xs font-medium" onClick={() => openDetail(r)}>
                        {r.nombre} {r.apellido}
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="flex items-center gap-1.5">
                          <a href={`mailto:${r.correo}`} className="text-blue-600 hover:underline">{r.correo}</a>
                          <button
                            onClick={(e) => { e.stopPropagation(); copyEmail(r.correo); }}
                            className="text-slate-400 hover:text-slate-600"
                            title="Copiar correo"
                          >
                            <Copy size={12} />
                          </button>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs" onClick={() => openDetail(r)}>
                        <div>{r.institucion}</div>
                        {r.tipo_institucion && (
                          <div className="text-[10px] text-slate-400">{r.tipo_institucion}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs" onClick={() => openDetail(r)}>{r.cargo || '—'}</TableCell>
                      <TableCell className="text-xs" onClick={() => openDetail(r)}>{r.cantidad_usuarios || '—'}</TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDetail(r)}
                            className="h-7 w-7 p-0"
                            title="Ver detalle"
                            data-testid={`req-view-${r.id}`}
                          >
                            <Eye size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeRequest(r.id)}
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                            title="Eliminar"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Detail modal */}
          <Dialog open={!!detailRequest} onOpenChange={(o) => !o && setDetailRequest(null)}>
            <DialogContent className="sm:max-w-2xl max-h-[90vh]" data-testid="req-detail-modal">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  Solicitud de {detailRequest?.nombre} {detailRequest?.apellido}
                  {detailRequest && (
                    <Badge variant="outline" className={ESTADO_META[detailRequest.estado || 'nueva']?.color}>
                      {ESTADO_META[detailRequest.estado || 'nueva']?.label}
                    </Badge>
                  )}
                </DialogTitle>
              </DialogHeader>
              {detailRequest && (
                <div className="space-y-4 py-2 overflow-y-auto max-h-[65vh] pr-2">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-[11px] text-slate-500 uppercase tracking-wide">Correo</div>
                      <div className="flex items-center gap-2">
                        <a href={`mailto:${detailRequest.correo}`} className="text-blue-600 hover:underline">
                          {detailRequest.correo}
                        </a>
                        <button onClick={() => copyEmail(detailRequest.correo)} className="text-slate-400 hover:text-slate-600">
                          <Copy size={12} />
                        </button>
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] text-slate-500 uppercase tracking-wide">Teléfono</div>
                      <div className="flex items-center gap-2">
                        {detailRequest.telefono ? (
                          <>
                            <a href={`tel:${detailRequest.telefono}`} className="text-blue-600 hover:underline">
                              {detailRequest.telefono}
                            </a>
                            <Phone size={12} className="text-slate-400" />
                          </>
                        ) : <span className="text-slate-400">—</span>}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] text-slate-500 uppercase tracking-wide">Institución</div>
                      <div>{detailRequest.institucion}</div>
                    </div>
                    <div>
                      <div className="text-[11px] text-slate-500 uppercase tracking-wide">Tipo institución</div>
                      <div>{detailRequest.tipo_institucion || '—'}</div>
                    </div>
                    <div>
                      <div className="text-[11px] text-slate-500 uppercase tracking-wide">Cargo</div>
                      <div>{detailRequest.cargo || '—'}</div>
                    </div>
                    <div>
                      <div className="text-[11px] text-slate-500 uppercase tracking-wide">Cantidad usuarios</div>
                      <div>{detailRequest.cantidad_usuarios || '—'}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-[11px] text-slate-500 uppercase tracking-wide">Fecha de solicitud</div>
                      <div>{new Date(detailRequest.created_at).toLocaleString('es-CL')}</div>
                    </div>
                  </div>

                  {detailRequest.mensaje && (
                    <div>
                      <div className="text-[11px] text-slate-500 uppercase tracking-wide mb-1">Mensaje</div>
                      <div className="p-3 bg-slate-50 rounded-md border border-slate-200 text-sm whitespace-pre-wrap">
                        {detailRequest.mensaje}
                      </div>
                    </div>
                  )}

                  <div>
                    <div className="text-[11px] text-slate-500 uppercase tracking-wide mb-1">Cambiar estado</div>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(ESTADO_META).map(([k, m]) => (
                        <Button
                          key={k}
                          variant={detailRequest.estado === k ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => updateRequestField(detailRequest.id, { estado: k })}
                          className="h-8 text-xs"
                          data-testid={`req-detail-status-${k}`}
                        >
                          {m.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="notes" className="text-[11px] text-slate-500 uppercase tracking-wide">
                      Notas internas
                    </Label>
                    <Textarea
                      id="notes"
                      value={detailNotes}
                      onChange={(e) => setDetailNotes(e.target.value)}
                      rows={4}
                      placeholder="Registra el seguimiento comercial, próximos pasos, contactos…"
                      className="mt-1"
                      data-testid="req-detail-notes"
                    />
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setDetailRequest(null)}>Cerrar</Button>
                <Button onClick={saveDetail} disabled={detailSaving} data-testid="req-detail-save">
                  {detailSaving && <Loader2 size={14} className="animate-spin mr-1" />}
                  Guardar notas
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
}
